/**
 * NES master palette: 64 fixed colors indexed 0..63.
 *
 * Values approximate a standard 2C02 palette (Nestopia / common reference).
 * Indices 0x0D, 0x0E, 0x0F (and 0x1D etc.) are blacker-than-black on real
 * hardware; we render them as black for safety since some entries are
 * undefined / dangerous.
 */

export const NES_MASTER_PALETTE_HEX: readonly string[] = [
    "#666666", "#002A88", "#1412A7", "#3B00A4", "#5C007E", "#6E0040", "#6C0600", "#561D00",
    "#333500", "#0B4800", "#005200", "#004F08", "#00404D", "#000000", "#000000", "#000000",
    "#ADADAD", "#155FD9", "#4240FF", "#7527FE", "#A01ACC", "#B71E7B", "#B53120", "#994E00",
    "#6B6D00", "#388700", "#0C9300", "#008F32", "#007C8D", "#000000", "#000000", "#000000",
    "#FFFEFF", "#64B0FF", "#9290FF", "#C676FF", "#F36AFF", "#FE6ECC", "#FE8170", "#EA9E22",
    "#BCBE00", "#88D800", "#5CE430", "#45E082", "#48CDDE", "#4F4F4F", "#000000", "#000000",
    "#FFFEFF", "#C0DFFF", "#D3D2FF", "#E8C8FF", "#FBC2FF", "#FEC4EA", "#FECCC5", "#F7D8A5",
    "#E4E594", "#CFEF96", "#BDF4AB", "#B3F3CC", "#B5EBF2", "#B8B8B8", "#000000", "#000000",
];

export interface RgbColor {
    r: number;
    g: number;
    b: number;
}

let cachedRgb: RgbColor[] | null = null;

/** Returns the master palette as 64 RGB tuples. */
export function getNesMasterRgb(): RgbColor[] {
    if (cachedRgb) return cachedRgb;
    cachedRgb = NES_MASTER_PALETTE_HEX.map(parseHexRgb);
    return cachedRgb;
}

function parseHexRgb(hex: string): RgbColor {
    const h = hex.replace(/^#/, "");
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
}

/**
 * Returns the master-palette index closest to the given RGB color
 * (squared Euclidean distance). Indices 0x0D..0x0F, 0x1D..0x1F, 0x2D..0x2F,
 * 0x3D..0x3F are excluded so we never pick a "blacker-than-black" sentinel.
 */
export function nearestNesIndex(r: number, g: number, b: number): number {
    const rgb = getNesMasterRgb();
    let bestIdx = 0x0F & 0; // safe black at index 0x0F not used; default to 0x0D... actually pick 0x1D = dark gray? Use 0x0F mapped to black.
    let bestDist = Infinity;
    for (let i = 0; i < rgb.length; i++) {
        if (isReservedBlack(i)) continue;
        const c = rgb[i];
        const dr = c.r - r;
        const dg = c.g - g;
        const db = c.b - b;
        const d = dr * dr + dg * dg + db * db;
        if (d < bestDist) {
            bestDist = d;
            bestIdx = i;
        }
    }
    return bestIdx;
}

/**
 * Master indices that map to "blacker than black" / undefined slots on real
 * hardware. We avoid them when picking colors so quantization never lands on
 * a reserved entry.
 */
export function isReservedBlack(index: number): boolean {
    const lo = index & 0x0F;
    return lo === 0x0D || lo === 0x0E || lo === 0x0F;
}

/** Returns the RGB hex string for a given master-palette index. */
export function nesIndexToHex(index: number): string {
    const safe = ((index % 64) + 64) % 64;
    return NES_MASTER_PALETTE_HEX[safe];
}

/** Pack `(255 << 24) | (r << 16) | (g << 8) | b` (matches `pixelSvgRender`). */
export function nesIndexToPackedRgba(index: number): number {
    const c = getNesMasterRgb()[((index % 64) + 64) % 64];
    return (
        ((255 & 0xff) << 24) |
        ((c.r & 0xff) << 16) |
        ((c.g & 0xff) << 8) |
        (c.b & 0xff)
    ) >>> 0;
}
